import type { ConstruccionDeInforme } from "@/lib/informes";
import { numeroEscrito } from "@/lib/numeroEscrito";
import type {
  CampoDeDatoDeConstruccion,
  CampoDeTerreno,
  ItemDeTerreno,
  RegistroDeTerrenoDelInforme,
  SeccionDeTerreno,
} from "@/lib/terreno";

import { formularioDesdeElItemDeTerreno, type FormularioDeConstruccion } from "./construcciones";
import { CAMPOS_DE_CONSTRUCCION } from "./notasDeConstruccion";
import { fotosDelRegistro } from "./registroDeTerreno";
import { formatearCifra } from "@/lib/montos";
import type { EstadoDelTerreno } from "./terrenoDeAntecedentes";
import { vistaDeTerreno } from "./terrenoDeAntecedentes";

/* ---------------------------------------------------------------------------
   El cuadro de construcciones: lo que se DERIVA de las filas del informe y de
   lo que el encargado registró en terreno.

   Es lógica PURA y vive fuera del componente por la razón de siempre: la regla
   de "qué se ve bajo este filtro", el orden de "pendientes primero" o la cuenta
   de un contador son las que pueden estar mal sin que nada se vea roto, y
   dentro del árbol un test no distingue una cifra buena de una cercana.
--------------------------------------------------------------------------- */

/** Cuántas filas entran por página: "Cargar 30 más". */
export const PAGINA_DEL_CUADRO = 30;

/**
 * En qué estado está el cruce con la visita de terreno.
 *
 * - `oculto`: el módulo no está, carga o falló. Ninguna pieza de terreno se
 *   dibuja y el cuadro es el de siempre: la pantalla no puede depender de él.
 * - `pendiente`: el módulo está y el encargado nunca sincronizó. Se dice
 *   "Visita pendiente" y no "Sin registros": lo segundo afirmaría que fue y no
 *   anotó nada.
 * - `sincronizado`: hubo al menos una sincronización.
 */
export type ModoDelTerreno = "oculto" | "pendiente" | "sincronizado";

export function modoDelTerreno(terreno: EstadoDelTerreno | undefined): ModoDelTerreno {
  if (!terreno || terreno.estado !== "ok") return "oculto";

  return vistaDeTerreno(terreno.registro).sincronizado ? "sincronizado" : "pendiente";
}

const ETIQUETAS_DE_LAS_NOTAS: Record<CampoDeDatoDeConstruccion, string> = {
  tipo: "Tipo de edificación",
  anio: "Año",
  superficie: "Superficie",
  clase: "Clase / calidad",
};

/** El campo del formulario sobre el que cae cada nota de terreno. */
export const CLAVE_DEL_FORMULARIO: Record<CampoDeDatoDeConstruccion, keyof FormularioDeConstruccion> = {
  tipo: "tipo_edificacion",
  anio: "anio",
  superficie: "superficie_m2",
  clase: "clase",
};

export interface NotaDeLaFila {
  campo: CampoDeDatoDeConstruccion;
  etiqueta: string;
  texto: string;
  capturado_en: string | null;
}

export interface RegistrosDeLaFila {
  fotos: number;
  audios: number;
  textos: number;
  total: number;
}

/**
 * Una fila del cuadro tal como la pantalla la necesita: la fila guardada (o la
 * construcción de terreno que todavía no lo es) con lo de terreno ya contado.
 */
export interface FilaDelCuadro {
  /** `web:<id>` o `terreno:<uuid>`: la misma referencia que usa el registro de terreno. */
  clave: string;
  /** El id de la fila del cuadro, o `null` si es una construcción de terreno sin guardar. */
  id: number | null;
  /** El uuid de la construcción de terreno, enlazada o no. */
  terrenoRef: string | null;
  fila: ConstruccionDeInforme | null;
  item: ItemDeTerreno | null;
  tipo: string;
  superficie: number | null;
  /** El total que calculó el servidor. `null`: sin valorizar. */
  total: number | null;
  validada: boolean;
  /** Se muestra la insignia TERRENO: nació en la App o está enlazada a algo de ella. */
  deTerreno: boolean;
  registros: RegistrosDeLaFila;
  notas: NotaDeLaFila[];
}

function contarRegistros(item: ItemDeTerreno | null): RegistrosDeLaFila {
  const registros = item?.registros ?? [];
  const fotos = registros.reduce((n, r) => n + fotosDelRegistro(r).length, 0);
  const audios = registros.reduce((n, r) => n + r.audios.length, 0);
  const textos = registros.filter((r) => r.texto !== null).length;

  return { fotos, audios, textos, total: fotos + audios + textos };
}

function notasDelItem(item: ItemDeTerreno | null): NotaDeLaFila[] {
  const notas = item?.notas ?? {};

  return CAMPOS_DE_CONSTRUCCION.flatMap((campo) => {
    const nota = notas[campo];

    return nota
      ? [
          {
            campo,
            etiqueta: ETIQUETAS_DE_LAS_NOTAS[campo],
            texto: nota.texto,
            capturado_en: nota.capturado_en,
          },
        ]
      : [];
  });
}

/**
 * Las filas del cuadro: primero las guardadas, en el orden que trae el
 * servidor, y después las construcciones que el encargado creó en terreno y
 * todavía no se guardaron (`nuevo` y sin fila enlazada).
 *
 * Una construcción de terreno YA enlazada no aparece dos veces: el servidor
 * cuelga sus registros y notas de la fila `web:<id>` y deja de listarla aparte.
 */
export function filasDelCuadro(
  filas: ConstruccionDeInforme[],
  seccion: SeccionDeTerreno | null,
): FilaDelCuadro[] {
  const items = seccion?.items ?? [];
  const delCuadro: FilaDelCuadro[] = filas.map((fila) => {
    const item = items.find((i) => i.referencia === `web:${fila.id}`) ?? null;

    return {
      clave: `web:${fila.id}`,
      id: fila.id,
      terrenoRef: fila.terreno_ref,
      fila,
      item,
      tipo: fila.tipo_edificacion,
      superficie: fila.superficie_m2 === null ? null : Number(fila.superficie_m2),
      total: fila.total_uf,
      validada: fila.validada,
      deTerreno: fila.terreno_ref !== null,
      registros: contarRegistros(item),
      notas: notasDelItem(item),
    };
  });

  const sinGuardar: FilaDelCuadro[] = items
    .filter((i) => i.nuevo && i.referencia.startsWith("terreno:"))
    .map((item) => {
      const formulario = formularioDesdeElItemDeTerreno(item);

      return {
        clave: item.referencia,
        id: null,
        terrenoRef: item.referencia.slice("terreno:".length),
        fila: null,
        item,
        tipo: formulario.tipo_edificacion,
        superficie: numeroEscrito(formulario.superficie_m2),
        total: null,
        validada: false,
        deTerreno: true,
        registros: contarRegistros(item),
        notas: notasDelItem(item),
      };
    });

  return [...delCuadro, ...sinGuardar];
}

/** La sección de construcciones del registro de terreno, o `null`. */
export function seccionDeConstrucciones(
  registro: RegistroDeTerrenoDelInforme | undefined,
): SeccionDeTerreno | null {
  return registro?.secciones.find((s) => s.clave === "construcciones") ?? null;
}

/** Lo que el encargado dijo de la descripción general de las construcciones. */
export function campoDeLaDescripcionDeConstrucciones(
  registro: RegistroDeTerrenoDelInforme | undefined,
): CampoDeTerreno | null {
  return seccionDeConstrucciones(registro)?.campos.find((c) => c.clave === "construcciones") ?? null;
}

/* --- Filtros, orden y paginación --- */

export type FiltroDelCuadro =
  | "todas"
  | "con_registros"
  | "sin_registros"
  | "pendiente"
  | "con_notas"
  | "sin_valorizar"
  | "sin_validar";

export type OrdenDelCuadro = "pendientes" | "nombre" | "superficie";

function coincideConElFiltro(
  fila: FilaDelCuadro,
  filtro: FiltroDelCuadro,
  modo: ModoDelTerreno,
): boolean {
  const sincronizado = modo === "sincronizado";

  switch (filtro) {
    case "todas":
      return true;
    case "con_registros":
      return sincronizado && fila.registros.total > 0;
    case "sin_registros":
      return sincronizado && fila.registros.total === 0;
    case "pendiente":
      return modo === "pendiente";
    case "con_notas":
      return sincronizado && fila.notas.length > 0;
    case "sin_valorizar":
      return fila.total === null;
    case "sin_validar":
      return !fila.validada;
  }
}

/**
 * Qué tan urgente es una fila: con notas de terreno sin validar, sin total,
 * sin validar, y el resto. Las notas solo pesan con la visita sincronizada.
 */
function urgencia(fila: FilaDelCuadro, modo: ModoDelTerreno): number {
  if (modo === "sincronizado" && fila.notas.length > 0 && !fila.validada) return 0;
  if (fila.total === null) return 1;

  return fila.validada ? 3 : 2;
}

function porNombre(a: FilaDelCuadro, b: FilaDelCuadro): number {
  return a.tipo.localeCompare(b.tipo, "es");
}

const FILTROS: FiltroDelCuadro[] = [
  "todas",
  "con_registros",
  "sin_registros",
  "pendiente",
  "con_notas",
  "sin_valorizar",
  "sin_validar",
];

export interface ResumenDelCuadro {
  cantidad: number;
  /** Metros cuadrados construidos: la suma de las superficies cargadas. */
  superficie: number;
  sinValorizar: number;
  validadas: number;
  /** La suma de los totales que calculó el servidor; las sin valorizar no entran. */
  totalUf: number;
}

export interface VistaDelCuadro {
  /** Lo que se dibuja: ya filtrado, ordenado y recortado a la página. */
  visibles: FilaDelCuadro[];
  /** Cuántas hay bajo el filtro y la búsqueda, antes de recortar. */
  coincidentes: number;
  restan: number;
  /** "30 de 70 · quedan 40", o "" si no hay nada. */
  mostrando: string;
  /** Cuántas trae el próximo "Cargar más" (0 si no queda ninguna). */
  cargarMas: number;
  /** El conteo de cada filtro. NO depende del filtro ni de la búsqueda puestos. */
  conteos: Record<FiltroDelCuadro, number>;
  resumen: ResumenDelCuadro;
}

export function vistaDelCuadro(
  filas: FilaDelCuadro[],
  {
    modo,
    busqueda,
    filtro,
    orden,
    pagina,
  }: {
    modo: ModoDelTerreno;
    busqueda: string;
    filtro: FiltroDelCuadro;
    orden: OrdenDelCuadro;
    pagina: number;
  },
): VistaDelCuadro {
  const q = busqueda.trim().toLowerCase();

  const coincidentes = filas
    .filter((f) => coincideConElFiltro(f, filtro, modo))
    .filter((f) => q === "" || f.tipo.toLowerCase().includes(q))
    .sort((a, b) => {
      if (orden === "superficie") return (b.superficie ?? 0) - (a.superficie ?? 0);
      if (orden === "nombre") return porNombre(a, b);

      return urgencia(a, modo) - urgencia(b, modo) || porNombre(a, b);
    });

  const visibles = coincidentes.slice(0, pagina * PAGINA_DEL_CUADRO);
  const restan = coincidentes.length - visibles.length;

  const conteos = Object.fromEntries(
    FILTROS.map((k) => [k, filas.filter((f) => coincideConElFiltro(f, k, modo)).length]),
  ) as Record<FiltroDelCuadro, number>;

  return {
    visibles,
    coincidentes: coincidentes.length,
    restan,
    mostrando:
      coincidentes.length === 0
        ? ""
        : `${visibles.length} de ${coincidentes.length}${restan > 0 ? ` · quedan ${restan}` : ""}`,
    cargarMas: Math.min(PAGINA_DEL_CUADRO, restan),
    conteos,
    resumen: {
      cantidad: filas.length,
      superficie: filas.reduce((n, f) => n + (f.superficie ?? 0), 0),
      sinValorizar: filas.filter((f) => f.total === null).length,
      validadas: filas.filter((f) => f.validada).length,
      totalUf: filas.reduce((n, f) => n + (f.total ?? 0), 0),
    },
  };
}

/** "540 m² construidos · 2 sin valorizar", o la invitación a cargar la primera. */
export function textoDelResumen(resumen: ResumenDelCuadro): string {
  if (resumen.cantidad === 0) {
    return "Agrega la primera construcción o usa Rellenar con IA.";
  }

  const sin = resumen.sinValorizar > 0 ? ` · ${resumen.sinValorizar} sin valorizar` : "";

  return `${formatearCifra(String(resumen.superficie))} m² construidos${sin}`;
}
